import { useEffect, useState } from "react";
import { GridRowsProp } from "@mui/x-data-grid";
import { Box, Button, Paper, Fade } from "@mui/material";
import QRCode from "react-qr-code";
import { useTheme } from "@mui/material/styles";

import { CustomGridColDef, DataGridCustom } from "../../common/Datagrid";
import { getProjects } from "./routes/getShipments";
import { BaseModal } from "../../common/Modals";
import ShipmentForm from "./ShipmentForm";
import ExcelExtractedDataTable from "../../qrcode/ExcelExtractedDataTable";
import { ExcelData } from "../../qrcode/ExcelData";
import handleDownload from "../../qrcode/handleImgDownload";

const ProjectList: React.FC = ({}) => {
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRowClicked, setIsRowClicked] = useState(false);
  const [isQrPresent, setIsQrPresent] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<ExcelData | null>(null);
  const handleQrCodePresent = () => setIsQrPresent(true);
  const theme = useTheme();

  useEffect(() => {
    document.title = "Nanesa - Spedizioni"; // Set the browser tab title
  }, []);

  // --- Open and close the modal ---

  const openModalForQr = () => {
    setSelectedRowData(null);
    setIsRowClicked(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // --- Fetch data from the API ---
  const fetchProject = async (search: string) => {
    const res: Response = await getProjects(search);
    const getProjectData = await res.json();
    if (res.ok) {
      const js = getProjectData.returnValue.rows;
      setRows(js); // Aggiorna lo stato con i dati ottenuti
    } else {
      alert(getProjectData.message);
    }
  };

  // --- Handle row click to show datas ---
  const handleRowClick = (row: ExcelData) => {
    setSelectedRowData(row);
    setIsRowClicked(true);
    setIsModalOpen(true);
    setIsQrPresent(true);
  };

  // --- All the columns ---
  const columns: CustomGridColDef[] = [
    {
      field: "code",
      flex: 1,
      importance: 0,
    },
    {
      field: "id",
      importance: 0,
    },
    {
      field: "articolo",
      headerName: "Prodotto",
      flex: 1,
      editable: false,
      importance: 0,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "lotto",
      headerName: "Lotto",
      flex: 1,
      editable: false,
      importance: 650,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "dataProduzione",
      headerName: "Data Produzione",
      type: "string",
      flex: 1,
      editable: false,
      importance: 1000,
      headerAlign: "center",
      valueFormatter: (value: any) => {
        // When the data is fetched from the backend, it's a string.
        // Remove timezone information to prevent date shifting
        if (!value) return '';
        const stringValue = String(value);
        // Remove timezone 'Z' and time portion if present
        const cleanValue = stringValue.replace('Z', '').replace(/T.*$/, '');
        
        // Parse the date parts manually to avoid timezone issues
        const dateParts = cleanValue.includes('-') ? cleanValue.split('-') : null;
        if (dateParts && dateParts.length === 3) {
          // ISO format: YYYY-MM-DD
          const year = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1; // Month is 0-based
          const day = parseInt(dateParts[2]);
          const date = new Date(year, month, day);
          return date.toLocaleDateString("it-IT");
        }
        
        // Fallback to original logic if format is unexpected
        const date = new Date(cleanValue);
        return date.toLocaleDateString("it-IT");
      },
      align: "center",
    },
    {
      field: "quantita",
      headerName: "Quantità",
      type: "string",
      flex: 0.7,
      editable: false,
      importance: 1000,
      headerAlign: "center",
      align: "right",
    },
    {
      field: "coa",
      headerName: "COA",
      flex: 1,
      editable: false,
      importance: 650,
      headerAlign: "center",
      align: "right",
    },
    {
      field: "numeroOrdine",
      headerName: "Numero d'ordine",
      flex: 1,
      editable: false,
      importance: 0,
      headerAlign: "center",
      align: "right",
    },
    {
      field: "numeroDdt",
      headerName: "Numero DDT",
      flex: 0.7,
      editable: false,
      importance: 650,
      headerAlign: "center",
      align: "right",
    },
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 1,
      editable: false,
      importance: 1000,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "sds",
      headerName: "Scheda di Sicurezza",
      flex: 1,
      editable: false,
      importance: 1000,
      headerAlign: "center",
      align: "left",
    },
  ];

  return (
    <Box>
      <Paper elevation={1} sx={{ padding: 3, margin: 3 }}>
        <Box>
          {/* ----- Datagrid configuration ----- */}
          <DataGridCustom
            rows={rows as any}
            columns={columns.filter((col) => col.field !== "id" && col.field !== "code")}
            onRowClick={(params) => handleRowClick(params.row)}
            checkboxSelection={false}
            title={"Spedizioni"}
            action={openModalForQr}
            actionTitle={"Crea nuova"}
            actionSearch={fetchProject}
            hiddenSearchButton={true}
            actionSearchTitle={"Cerca"}
          ></DataGridCustom>
        </Box>
      </Paper>
      <BaseModal
        /* ----- Upload new Excel file ----- */
        title={!isQrPresent ? "Carica un nuovo file" : ""}
        isOpen={isModalOpen}
        onClose={() => {
          closeModal();
          setIsQrPresent(false);
        }}
        closeButton={false}
        className={"modal-box-project"}
      >
        <Fade in={isModalOpen} timeout={500}>
          <div>
            {isRowClicked && selectedRowData ?
              <div>
                {/* ----- QR Code Container ----- */}
                <div
                  id="qr-code-container"
                  style={{
                    display: "inline-block",
                    background: "white",
                    padding: 16,
                    borderRadius: 10,
                  }}
                >
                  {/* ----- QR Code ----- */}
                  <QRCode
                    id="qr-code"
                    value={`https://nanesa.azurewebsites.net/check/qrCode?qrCode=${selectedRowData.code}`}
                    size={224}
                    style={{
                      background: "white",
                    }}
                  />
                </div>

                {/* ----- ExcelExtractedDataTable ----- */}
                <ExcelExtractedDataTable excelData={selectedRowData} />
                <Box
                  component="form"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  {/* Download Button */}
                  <Button
                    sx={{
                      paddingRight: 3,
                      paddingLeft: 3,
                      height: "40px",
                      backgroundColor: theme.palette.mode === "dark" ? "rgb(98, 98, 98)" : "rgb(239,239,239)",
                      color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                    }}
                    onClick={() => handleDownload("qr-code-container")}
                    variant="contained"
                  >
                    Download
                  </Button>

                  {/* Print Button */}
                  <Button
                    sx={{
                      paddingRight: 3,
                      paddingLeft: 3,
                      height: "40px",
                      backgroundColor: theme.palette.mode === "dark" ? "rgb(98, 98, 98)" : "rgb(239,239,239)",
                      color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                    }}
                    onClick={() => {
                      const qrCodeElement = document.getElementById("qr-code");
                      if (qrCodeElement) {
                        const printWindow = window.open("", "");
                        if (printWindow) {
                          printWindow.document.body.innerHTML = qrCodeElement.outerHTML;
                          printWindow.print();
                          printWindow.close();
                        }
                      }
                    }}
                    variant="contained"
                  >
                    Stampa
                  </Button>
                </Box>
              </div>
            : <ShipmentForm
                twoColumns={true}
                excelData={null}
                onQrCodePresent={handleQrCodePresent}
                onCancel={() => {
                  closeModal();
                  setIsQrPresent(false);
                }}
                onUpdateShipments={() => fetchProject("")}
              />
            }
          </div>
        </Fade>
      </BaseModal>
    </Box>
  );
};

export default ProjectList;
